/**
 * Thin facade over the cockpit axios instance. Exposes
 * apiGet/apiPost/apiPut/apiPatch/apiDelete/apiGetRaw/apiPostBlob/apiPostFormData
 * to the lib/api/clients/* layer; auth rides on the HttpOnly cookie via
 * `withCredentials: true`. Errors are normalised so callers see the
 * backend's i18n key (`errorCode`) on `error.message`.
 */

import type { AxiosError, AxiosResponse } from "axios";
import { api } from "@/lib/api/client";

type Headers = Record<string, string>;

/**
 * Convert axios errors into a plain Error whose `.message` carries the i18n
 * key the backend returned (`response.data.errorCode`). Without this every
 * caller's `error.message` reads "Request failed with status code 401" and
 * fails translation.
 *
 * Also forwards `retryAfter` / `violations` / `violationsTruncated` so
 * `handleErrorServer` can promote them on its `ServerError` shape.
 *
 * Exported for the ONE client that bypasses the shim's helpers (AiClient
 * talks to `api` directly for its streaming needs) so its one-shot endpoints
 * throw the same shape as everything else (audit 2026-09-28, M1).
 */
export function rethrowApi(error: unknown): never {
  const ax = error as AxiosError<{
    errorCode?: unknown;
    message?: unknown;
    retryAfter?: unknown;
    violations?: unknown;
    violationsTruncated?: unknown;
    failures?: unknown;
    capacityContext?: unknown;
    missingContactIds?: unknown;
    suppressedRecipients?: unknown;
    candidates?: unknown;
  }>;
  const data = ax?.response?.data;
  let message = "unknown";
  let detail: string | undefined;
  if (data && typeof data.errorCode === "string" && data.errorCode.length > 0) {
    message = data.errorCode;
    // The backend envelope carries BOTH the resolved i18n key (`errorCode`)
    // and the raw human-readable message. Keep the raw text as `detail` so
    // generic catch-all codes (`validationError`) can still tell the user
    // WHICH field was rejected instead of a bare "Validation error" toast.
    if (
      typeof data.message === "string" &&
      data.message.length > 0 &&
      data.message !== message
    ) {
      detail = data.message;
    }
  } else if (data && typeof data.message === "string" && data.message.length > 0) {
    message = data.message;
  } else if (error instanceof Error && error.message) {
    message = error.message;
  }
  const wrapped = new Error(message) as Error & {
    status?: number;
    detail?: string;
    retryAfter?: number;
    violations?: unknown;
    violationsTruncated?: boolean;
    failures?: unknown;
    capacityContext?: unknown;
    missingContactIds?: unknown;
    suppressedRecipients?: unknown;
    candidates?: unknown;
    response?: unknown;
    cause?: unknown;
  };
  // Attach ALL rich envelope fields as own properties (not only via
  // `.response`) so they survive a handleError re-wrap, which copies own
  // fields but not the axios response reference.
  //
  // The status is one of them, and it was missing here: on the direct-throw
  // path (every `clients/*` helper) only the axios response carried it, so a
  // caller reading `err.status` alone read undefined. `handleError` has been
  // re-deriving it from `.response` for a while (ErrorHandler: "the bare status
  // is safe and is the one breadcrumb worth keeping"), so the gap was never
  // repo-wide — but `copyRichFields` does NOT carry it, so it is still lost
  // through `handleErrorServer` / a second re-wrap. Setting it at the source is
  // the same reasoning as every other field here.
  //
  // ⚠ This wakes up branches that read `err.status` alone and have therefore
  // never fired on this path — `CompanyHandling.pollDeletionStatus` treats a
  // 401 as "the cascade finished", which is what its comment intends and what
  // it could not do before.
  if (typeof ax?.response?.status === "number") wrapped.status = ax.response.status;
  if (detail !== undefined) wrapped.detail = detail;
  if (data && typeof data.retryAfter === "number") wrapped.retryAfter = data.retryAfter;
  // …and otherwise from the HTTP header, which is where it actually comes from.
  //
  // No endpoint in main-backend puts `retryAfter` in the JSON body — measured:
  // `grep '"retryAfter"' src/main/java` is empty. RateLimitService.build429
  // sends `Retry-After` as a header and an ErrorResponse body of
  // {errorCode, message, missingCapability}. So the line above never fired, and
  // every countdown in this app — login lockout, password reset, password
  // change, invite register, register, test send — has been rendering its copy
  // without the number since it was written.
  //
  // Body first, so anything that already worked keeps working. RFC 9110 also
  // permits an HTTP-date here; a countdown cannot use one, and 0 means "no
  // wait", so both fall through to undefined rather than rendering "0s".
  if (wrapped.retryAfter === undefined) {
    const header = (ax?.response?.headers as Record<string, unknown> | undefined)?.[
      "retry-after"
    ];
    const seconds = typeof header === "number" ? header : Number(header);
    if (typeof header !== "undefined" && String(header).trim() !== ""
        && Number.isFinite(seconds) && seconds > 0) {
      wrapped.retryAfter = seconds;
    }
  }
  if (data && Array.isArray(data.violations)) wrapped.violations = data.violations;
  if (data && typeof data.violationsTruncated === "boolean") wrapped.violationsTruncated = data.violationsTruncated;
  if (data && Array.isArray(data.failures)) wrapped.failures = data.failures;
  if (data && data.capacityContext && typeof data.capacityContext === "object")
    wrapped.capacityContext = data.capacityContext;
  if (data && Array.isArray(data.missingContactIds))
    wrapped.missingContactIds = data.missingContactIds;
  if (data && Array.isArray(data.suppressedRecipients))
    wrapped.suppressedRecipients = data.suppressedRecipients;
  if (data && Array.isArray(data.candidates))
    wrapped.candidates = data.candidates;
  // Keep the original axios shape reachable for callers that already inspect it.
  wrapped.response = ax?.response;
  wrapped.cause = error;
  throw wrapped;
}

function buildConfig(headers: Headers, opts?: { responseType?: "blob" | "arraybuffer"; signal?: AbortSignal }) {
  // Strip Authorization. Auth is HttpOnly cookies set by the backend and
  // attached automatically because axios uses `withCredentials: true`.
  // Legacy callers still thread `Authorization: Bearer ${accessToken}`
  // through these helpers; we drop it here so only the cookie reaches
  // the wire.
  const sanitized: Headers = {};
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === "authorization") continue;
    sanitized[k] = v;
  }
  return {
    headers: sanitized,
    ...(opts?.responseType ? { responseType: opts.responseType } : {}),
    ...(opts?.signal ? { signal: opts.signal } : {}),
  };
}

// Request bodies whose serialised JSON exceeds this size are gzip-compressed
// on the wire. JSON compresses ~10x — every object key is repeated on every
// row, and that redundancy collapses to almost nothing — so a multi-MB guest
// import rides in a few hundred KB and clears the backend body-size limit.
// The backend has `quarkus.http.enable-decompression=true`; small requests
// skip this so we don't pay compression CPU for no benefit.
const COMPRESS_THRESHOLD = 1_000_000; // 1 MB of serialised JSON

/**
 * Gzip-compress a large JSON request body. Returns the body untouched when
 * it's small, not a plain object, or when the browser lacks
 * `CompressionStream` (the backend still accepts uncompressed bodies up to
 * its limit, so an old browser degrades rather than breaks).
 */
async function maybeGzip(
  body: unknown,
  headers: Headers,
): Promise<{ data: unknown; headers: Headers }> {
  if (
    body == null ||
    typeof body !== "object" ||
    body instanceof Blob ||
    body instanceof FormData ||
    body instanceof ArrayBuffer ||
    body instanceof URLSearchParams ||
    typeof CompressionStream === "undefined"
  ) {
    return { data: body, headers };
  }
  const json = JSON.stringify(body);
  if (json.length < COMPRESS_THRESHOLD) {
    return { data: body, headers };
  }
  const gz = await new Response(
    new Blob([json]).stream().pipeThrough(new CompressionStream("gzip")),
  ).blob();
  return {
    data: gz,
    headers: {
      ...headers,
      "Content-Type": "application/json",
      "Content-Encoding": "gzip",
    },
  };
}

async function readBody<T>(response: AxiosResponse, json: boolean | undefined, cleanAdditionalFields?: boolean): Promise<T> {
  const data = response.data;

  if (json) {
    if (cleanAdditionalFields && Array.isArray(data)) {
      return data.map((item: unknown) => {
        if (item && typeof item === "object" && "additionalFields" in item) {
          const processedAdditionalFields: Record<string, unknown> = {};
          Object.entries(
            (item as { additionalFields: Record<string, unknown> }).additionalFields,
          ).forEach(([key, value]) => {
            if (typeof value === "string") {
              processedAdditionalFields[key] = value.replace(/[''"]/g, "");
            } else {
              processedAdditionalFields[key] = value;
            }
          });
          return { ...item, additionalFields: processedAdditionalFields };
        }
        return item;
      }) as T;
    }
    return data as T;
  }

  // axios returns string when transformResponse is bypassed for text responses;
  // most callers want raw text — pass through.
  if (typeof data === "string") return data as T;
  return data as T;
}

export async function apiGet<T>(
  url: string,
  headers: Headers = {},
  json?: boolean,
  cleanAdditionalFields?: boolean,
  /** `signal`: a read the caller may abandon (a timed-out uncached catalogue read). */
  opts?: { signal?: AbortSignal },
): Promise<T> {
  try {
    const r = await api.get(url, buildConfig(headers, opts?.signal ? { signal: opts.signal } : undefined));
    return readBody<T>(r, json, cleanAdditionalFields);
  } catch (e) { rethrowApi(e); }
}

export async function apiPost<T>(
  url: string,
  body: unknown,
  headers: Headers = {},
  json?: boolean,
  cleanAdditionalFields?: boolean,
  /** `signal`: a request the caller may abandon (a preview whose question changed). */
  opts?: { signal?: AbortSignal },
): Promise<T> {
  try {
    const gz = await maybeGzip(body, headers);
    const r = await api.post(url, gz.data, buildConfig(gz.headers, opts?.signal ? { signal: opts.signal } : undefined));
    return readBody<T>(r, json, cleanAdditionalFields);
  } catch (e) { rethrowApi(e); }
}

/**
 * `apiPost` that also hands back the RESPONSE headers, lower-cased as axios
 * delivers them. For the one caller whose body carries nothing but an id and
 * whose signal rides a header (the background-job submit's
 * `X-Veltric-Idempotent-Replay`). Only headers the backend exposes through
 * CORS are present in a browser; the caller treats absence as "no signal".
 */
export async function apiPostWithHeaders<T>(
  url: string,
  body: unknown,
  headers: Headers = {},
  /** `signal`: a submit the caller gives up on (the SMS send's 30 s client timeout). */
  opts?: { signal?: AbortSignal },
): Promise<{ data: T; headers: Record<string, string> }> {
  try {
    const gz = await maybeGzip(body, headers);
    const r = await api.post(url, gz.data, buildConfig(gz.headers, opts?.signal ? { signal: opts.signal } : undefined));
    const responseHeaders: Record<string, string> = {};
    for (const [k, v] of Object.entries(r.headers ?? {})) {
      if (v !== undefined && v !== null) responseHeaders[k.toLowerCase()] = String(v);
    }
    return { data: await readBody<T>(r, undefined), headers: responseHeaders };
  } catch (e) { rethrowApi(e); }
}

export async function apiPut<T>(
  url: string,
  body: unknown,
  headers: Headers = {},
  json?: boolean,
): Promise<T> {
  try {
    const r = await api.put(url, body, buildConfig(headers));
    return readBody<T>(r, json);
  } catch (e) { rethrowApi(e); }
}

export async function apiPatch<T>(
  url: string,
  body: unknown,
  headers: Headers = {},
  json?: boolean,
): Promise<T> {
  try {
    const r = await api.patch(url, body, buildConfig(headers));
    return readBody<T>(r, json);
  } catch (e) { rethrowApi(e); }
}

export async function apiDelete<T>(
  url: string,
  headers: Headers = {},
  body?: unknown,
  json?: boolean,
): Promise<T> {
  try {
    const r = await api.delete(url, {
      ...buildConfig(headers),
      ...(body !== undefined ? { data: body } : {}),
    });
    return readBody<T>(r, json);
  } catch (e) { rethrowApi(e); }
}

export async function apiGetRaw(
  url: string,
  headers: Headers = {},
): Promise<Response> {
  try {
    // axios returns AxiosResponse; we synthesize a fetch-Response-like shape.
    const r = await api.get(url, { ...buildConfig(headers), responseType: "arraybuffer" });
    const responseHeaders = new Headers();
    for (const [k, v] of Object.entries(r.headers)) {
      if (v !== undefined && v !== null) responseHeaders.set(k, String(v));
    }
    return new Response(r.data, {
      status: r.status,
      statusText: r.statusText,
      headers: responseHeaders,
    });
  } catch (e) { decodeBlobErrorBody(e); rethrowApi(e); }
}

/**
 * With `responseType: "arraybuffer"` axios decodes the body as binary even on
 * an error response, so `response.data` arrives as an ArrayBuffer and
 * `rethrowApi` can't read the JSON `errorCode` — every blob-endpoint error
 * would otherwise translate to the generic "unknown". Decode that buffer back
 * to its JSON object in place so the real i18n key reaches the caller.
 */
function decodeBlobErrorBody(error: unknown): void {
  const ax = error as AxiosError<unknown>;
  const data = ax?.response?.data;
  if (!(data instanceof ArrayBuffer)) return;
  try {
    const text = new TextDecoder().decode(data);
    (ax.response as { data: unknown }).data = text ? JSON.parse(text) : undefined;
  } catch {
    (ax.response as { data: unknown }).data = undefined;
  }
}

/**
 * POST twin of `apiGetRaw`: a fetch-like `Response` with the HEADERS kept, for
 * the one download whose filename the server chooses (`Content-Disposition`
 * of the company audit CSV). `apiPostBlob` hands back bytes alone, which is
 * right for every other blob endpoint and too little for this one. Error
 * bodies are decoded like every other blob helper's, so a 413 reaches the
 * caller as its `errorCode`, not as "unknown".
 */
export async function apiPostRaw(
  url: string,
  body: unknown,
  headers: Headers = {},
): Promise<Response> {
  try {
    const r = await api.post(url, body, {
      ...buildConfig(headers, { responseType: "arraybuffer" }),
    });
    const responseHeaders = new Headers();
    for (const [k, v] of Object.entries(r.headers)) {
      if (v !== undefined && v !== null) responseHeaders.set(k, String(v));
    }
    return new Response(r.data, {
      status: r.status,
      statusText: r.statusText,
      headers: responseHeaders,
    });
  } catch (e) { decodeBlobErrorBody(e); rethrowApi(e); }
}

export async function apiPostBlob(
  url: string,
  body: unknown,
  headers: Headers = {},
): Promise<Uint8Array> {
  try {
    const r = await api.post(url, body, {
      ...buildConfig(headers, { responseType: "arraybuffer" }),
    });
    return new Uint8Array(r.data as ArrayBuffer);
  } catch (e) { decodeBlobErrorBody(e); rethrowApi(e); }
}

export async function apiPostFormData<T>(
  url: string,
  body: FormData,
  headers: Headers = {},
  opts?: { responseType?: "text" | "blob" | "arraybuffer" },
): Promise<T> {
  const { "Content-Type": _ct, Authorization: _auth, ...rest } = headers;
  try {
    // Defense in depth against a future re-introduction of an instance-level
    // `Content-Type: application/json` default. Strip Content-Type inside
    // transformRequest (runs after header merge) so XHR auto-sets the proper
    // `multipart/form-data; boundary=…` for FormData payloads. Without this,
    // a JSON default would cause axios to JSON-stringify the FormData and
    // backends with @Consumes(MULTIPART_FORM_DATA) reject with 415.
    const r = await api.post(url, body, {
      headers: rest,
      ...(opts?.responseType ? { responseType: opts.responseType } : {}),
      transformRequest: [(data, hdrs) => {
        if (hdrs) {
          const h = hdrs as Record<string, unknown>;
          delete h["Content-Type"];
          delete h["content-type"];
        }
        return data;
      }],
    });
    return r.data as T;
  } catch (e) { rethrowApi(e); }
}
